/**
 * Wave 10 — Skeleton placeholder.
 *
 * Tiny, opinionated loading shimmer. We prefer skeletons over spinners
 * when the shape of the pending content is predictable (a list of cards,
 * a metric grid), because the eye reads them as "content is on the way"
 * rather than "something is stuck".
 *
 * Animation: a gentle pulse keyed off Tailwind's animate-pulse. We bake
 * the tone into the component so every caller gets the exact right
 * background (surface-3, lightly translucent) without re-inventing it.
 */

import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Override shape. Default is a rounded rectangle. */
  shape?: 'rect' | 'circle' | 'text';
}

export function Skeleton({
  className,
  shape = 'rect',
  ...rest
}: SkeletonProps) {
  return (
    <div
      aria-hidden
      className={cn(
        'animate-pulse bg-surface-3/70',
        shape === 'rect' && 'rounded-xl',
        shape === 'circle' && 'rounded-full',
        shape === 'text' && 'h-3 rounded-md',
        className,
      )}
      {...rest}
    />
  );
}

/**
 * Common composition: a stack of skeleton "lines" mimicking a text block.
 * Use when an EmptyState or spinner would over-communicate failure.
 */
export function SkeletonLines({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton
          key={i}
          shape="text"
          className={i === count - 1 ? 'w-2/3' : 'w-full'}
        />
      ))}
    </div>
  );
}

/** Card-shaped skeleton — use inside list rendering loops. */
export function SkeletonCard() {
  return (
    <div className="space-y-3 rounded-xl border border-line bg-surface p-3">
      <div className="flex items-center gap-3">
        <Skeleton shape="circle" className="h-8 w-8" />
        <Skeleton shape="text" className="w-32" />
      </div>
      <SkeletonLines count={2} />
    </div>
  );
}
